import React, { useMemo } from 'react';
import Button from '@app/react-common/components/button/Button';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { DisplayConditionsFormData } from '../BulkEditModal';
import { getConditionDisplayText, parseConditionKey, conditionMatches } from '../utils/conditionUtils';
import { CollapsibleSection } from '../components';
import { DISPLAY_CONDITIONS_ACTIONS } from '../utils/consts';
import { BasePreviewViewProps, SelectedMethodInfo } from './types';

interface DisplayConditionsPreviewViewProps extends BasePreviewViewProps {
  formData: DisplayConditionsFormData;
}

export const DisplayConditionsPreviewView: React.FC<DisplayConditionsPreviewViewProps> = ({
  onBack,
  onClose,
  onSaveChanges,
  formData,
  selectedMethods,
}) => {
  const conditionsToAdd = useMemo(() => {
    if (
      formData.action !== DISPLAY_CONDITIONS_ACTIONS.ADD &&
      formData.action !== DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL
    )
      return [];
    return formData.conditionGroups.flatMap((group) =>
      group.conditions.filter((c) => c.name && c.condition && c.values),
    );
  }, [formData]);

  const conditionsToRemove = useMemo(() => {
    if (formData.action !== DISPLAY_CONDITIONS_ACTIONS.REMOVE) return [];
    return formData.conditionsToRemove
      .map((key) => {
        const parsed = parseConditionKey(key);
        if (!parsed.name || !parsed.condition || !parsed.values) return null;
        return {
          name: parsed.name,
          displayName: parsed.name,
          condition: parsed.condition,
          values: parsed.values,
          weightUnit: parsed.weightUnit,
          optionalValue: parsed.optionalValue,
        };
      })
      .filter((c): c is NonNullable<typeof c> => c !== null);
  }, [formData]);

  const methodHasChanges = (method: SelectedMethodInfo): boolean => {
    if (
      formData.action === DISPLAY_CONDITIONS_ACTIONS.ADD ||
      formData.action === DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL
    ) {
      return conditionsToAdd.length > 0;
    }

    if (formData.action === DISPLAY_CONDITIONS_ACTIONS.REMOVE) {
      return conditionsToRemove.some((condToRemove) =>
        method.conditions?.some((existing) => conditionMatches(existing, condToRemove)),
      );
    }

    return false;
  };

  const changedMethods = useMemo(() => {
    return selectedMethods.filter(methodHasChanges);
  }, [selectedMethods, conditionsToAdd, conditionsToRemove]);

  const unchangedMethods = useMemo(() => {
    return selectedMethods.filter((m) => !methodHasChanges(m));
  }, [selectedMethods, conditionsToAdd, conditionsToRemove]);

  const handleSave = () => {
    const affectedIds = changedMethods.map((m) => m.id);
    onSaveChanges(affectedIds);
  };

  const isSaveDisabled = changedMethods.length === 0;
  const isAddAction =
    formData.action === DISPLAY_CONDITIONS_ACTIONS.ADD || formData.action === DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL;
  const isAppendToAllAction = formData.action === DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL;

  const getMethodGroupCount = (method: SelectedMethodInfo): number => {
    if (!method.conditions || method.conditions.length === 0) return 0;
    const validConditions = method.conditions.filter((c) => c.name !== 'locations');
    if (validConditions.length === 0) return 0;

    const groupIds = validConditions.map((c) => c.subsetGroupId);
    const hasNullGroup = groupIds.some((id) => id == null);
    const numericGroups = new Set(groupIds.filter((id) => id != null));

    return numericGroups.size + (hasNullGroup ? 1 : 0);
  };

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header bulk-edit-modal__header--with-subtitle">
        <div className="bulk-edit-modal__header__left">
          <div className="bulk-edit-modal__header__titles">
            <h2>Confirm changes</h2>
            <span className="bulk-edit-modal__header__subtitle">
              {changedMethods.length} of {selectedMethods.length} Methods Will Be Affected
            </span>
          </div>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        {changedMethods.length > 0 && (
          <CollapsibleSection title="Changed" count={changedMethods.length} defaultOpen={true}>
            {changedMethods.map((method) => (
              <div key={method.id} className="bulk-edit-modal__preview-item">
                <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>

                {isAddAction && (
                  <>
                    {isAppendToAllAction && (
                      <div className="bulk-edit-modal__preview-item__change">
                        <span className="bulk-edit-modal__preview-item__change__label">
                          {getMethodGroupCount(method) === 0
                            ? 'Creating 1 new group with:'
                            : `Adding to ${getMethodGroupCount(method)} existing group${getMethodGroupCount(method) !== 1 ? 's' : ''}:`}
                        </span>
                      </div>
                    )}
                    {conditionsToAdd.map((condition, i) => (
                      <div key={i} className="bulk-edit-modal__preview-item__change">
                        <span className="bulk-edit-modal__preview-item__change__label">Add Condition:</span>
                        <span className="bulk-edit-modal__preview-item__change__new">
                          {getConditionDisplayText(condition)}
                        </span>
                      </div>
                    ))}
                  </>
                )}

                {!isAddAction && (
                  <>
                    {conditionsToRemove
                      .filter((condToRemove) =>
                        method.conditions?.some((existing) => conditionMatches(existing, condToRemove)),
                      )
                      .map((condition, i) => (
                        <div key={i} className="bulk-edit-modal__preview-item__change">
                          <span className="bulk-edit-modal__preview-item__change__label">Remove Condition:</span>
                          <span className="bulk-edit-modal__preview-item__change__old">
                            {getConditionDisplayText(condition)}
                          </span>
                        </div>
                      ))}
                  </>
                )}
              </div>
            ))}
          </CollapsibleSection>
        )}
        {unchangedMethods.length > 0 && (
          <CollapsibleSection title="Unchanged" count={unchangedMethods.length} defaultOpen={false}>
            {unchangedMethods.map((method) => (
              <div key={method.id} className="bulk-edit-modal__preview-item">
                <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>
                <div className="bulk-edit-modal__preview-item__change">
                  <span className="bulk-edit-modal__preview-item__change__unchanged">(unchanged)</span>
                </div>
              </div>
            ))}
          </CollapsibleSection>
        )}
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Back" onClick={onBack} size="medium" variant="secondary" />
        {isSaveDisabled ? (
          <Tooltip tooltipText="No methods will be affected by this change" maxWidth={220}>
            <Button text="Save Changes" onClick={handleSave} size="medium" variant="primary" disabled />
          </Tooltip>
        ) : (
          <div className="bulk-edit-modal__button-wrapper">
            <Button text="Save Changes" onClick={handleSave} size="medium" variant="primary" />
          </div>
        )}
      </div>
    </div>
  );
};
